import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Button, Drawer, IconButton, Stack, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useHtsActions } from 'src/context/providers/HtsContext';

export const HTS_CODE_LENGTH = 12;

const EMPTY_HTS = { code: '', name: '' };

const HtsDrawer = ({ open, hts, onClose }) => {
  const { saveHts } = useHtsActions();
  const [form, setForm] = useState(EMPTY_HTS);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(hts ? { code: hts.code, name: hts.name } : EMPTY_HTS);
      setSubmitted(false);
    }
  }, [open, hts]);

  const missingCode = !form.code.trim();
  const missingName = !form.name.trim();
  const unusualLength = !missingCode && form.code.length !== HTS_CODE_LENGTH;

  const codeHelperText = () => {
    if (submitted && missingCode) return 'Code is required';
    if (unusualLength) {
      return `HTS codes have ${HTS_CODE_LENGTH} digits — this one has ${form.code.length}`;
    }
    return `Digits only, ${HTS_CODE_LENGTH} digits`;
  };

  const setField = (name) => (event) => setForm((prev) => ({ ...prev, [name]: event.target.value }));

  const handleSave = async () => {
    setSubmitted(true);
    if (missingCode || missingName) return;
    setSaving(true);
    const saved = await saveHts(hts?.id, { code: form.code.trim(), name: form.name.trim() });
    setSaving(false);
    if (saved) onClose();
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 460 } } }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="h6">{hts ? 'Edit HTS code' : 'New HTS code'}</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Stack spacing={2.5} sx={{ p: 2.5, flex: 1 }}>
        <TextField
          label="Code *"
          placeholder="610910004012"
          value={form.code}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, code: event.target.value.replace(/\D/g, '') }))
          }
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          error={submitted && missingCode}
          color={unusualLength ? 'warning' : 'primary'}
          focused={unusualLength || undefined}
          helperText={codeHelperText()}
          FormHelperTextProps={{ sx: unusualLength ? { color: 'warning.main' } : undefined }}
        />
        <TextField
          label="Name *"
          placeholder="Live horses, purebred breeding animals"
          value={form.name}
          onChange={setField('name')}
          multiline
          minRows={2}
          error={submitted && missingName}
          helperText={submitted && missingName ? 'Name is required' : ''}
        />
      </Stack>

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1.5}
        sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {hts ? 'Save' : 'Create'}
        </Button>
      </Stack>
    </Drawer>
  );
};

HtsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  hts: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

HtsDrawer.defaultProps = {
  hts: null,
};

export default HtsDrawer;
